import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Code, ArrowLeft, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const TeacherCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'teacher') {
      navigate('/login');
      return;
    }
    fetchCourse();
  }, [courseId, user, navigate]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/teacher/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!course) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/teacher/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Code className="w-6 h-6 text-violet-600" />
              <span className="text-lg font-bold text-slate-900">CodeITT</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-4xl">
        {/* Course Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-6" data-testid="course-header">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-white mb-4 ${
            course.level === 'beginner' ? 'bg-emerald-600' :
            course.level === 'intermediate' ? 'bg-amber-600' :
            'bg-red-600'
          }`}>
            {course.level.toUpperCase()}
          </span>
          <h1 className="text-4xl font-bold text-slate-900 mb-3">{course.title}</h1>
          <p className="text-lg text-slate-600 mb-4">{course.description}</p>
          <div className="flex items-center space-x-4 text-sm text-slate-500">
            <span>Created: {new Date(course.created_at).toLocaleDateString()}</span>
            <span>Updated: {new Date(course.updated_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Course Content */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="course-content">
          <div className="bg-slate-900 px-6 py-4 flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-bold text-white">Course Content</h2>
          </div>
          <div className="p-8">
            <div className="code-editor">
              <pre>{course.content}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherCoursePage;